'use client';

import { ChangeEvent, ReactElement, useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CountryIso2 } from 'react-international-phone';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Sentry from '@sentry/nextjs';
import { toast } from 'sonner';
import { z } from 'zod';

import { type AuthTokens, getAuthMnemonicFromError } from '@nutralab/api';
import { Button, FormGroup, Input, PasswordInput, PhoneInput, Text } from '@nutralab/ui';

import { classNames } from '@m1work/shared-utils';

import styles from './ResetPasswordModal.module.scss';

import { AuthModalLayout } from '@/app/_modals/_components/AuthModalLayout';
import { useAuth } from '@/app/_providers/AuthProvider';
import { useSiteConfig } from '@/app/_providers/SiteConfigProvider';
import { createBrowserApi, storefrontAuth } from '@/shared/api/browser';
import type { ModalProps } from '@/shared/types';
import { passwordField, phoneField, stringRequired, translateAuthMessage } from '@/shared/utils';

const RESEND_TIMEOUT_SECONDS = 60;

type Step = 'phone' | 'code' | 'newPassword';

const createPhoneSchema = (
    t: ReturnType<typeof useTranslation>['t'],
): z.ZodObject<{
    phone: z.ZodEffects<z.ZodString, string, string>;
}> =>
    z.object({
        phone: phoneField(t),
    });

type PhoneValues = z.infer<ReturnType<typeof createPhoneSchema>>;

const createCodeSchema = (
    t: ReturnType<typeof useTranslation>['t'],
): z.ZodObject<{
    code: z.ZodString;
}> =>
    z.object({
        code: stringRequired(t),
    });

type CodeValues = z.infer<ReturnType<typeof createCodeSchema>>;

const createNewPasswordSchema = (
    t: ReturnType<typeof useTranslation>['t'],
): z.ZodObject<{
    password: z.ZodString;
    confirmPassword: z.ZodString;
}> =>
    z.object({
        password: passwordField(t),
        confirmPassword: stringRequired(t),
    });

type NewPasswordValues = z.infer<ReturnType<typeof createNewPasswordSchema>>;

function formatCountdown(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60)
        .toString()
        .padStart(2, '0');
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
}

export interface ResetPasswordModalData {
    phone?: string;
}

type ResetPasswordModalProps = ModalProps<ResetPasswordModalData>;

/** Password reset: phone, then the SMS code, then the new password. */
export function ResetPasswordModal({ onClose, zIndex, data }: ResetPasswordModalProps): ReactElement {
    const { t, i18n } = useTranslation();
    const { country } = useSiteConfig();
    const { setSession } = useAuth();

    const [step, setStep] = useState<Step>('phone');
    const [phone, setPhone] = useState(data?.phone ?? '');
    const [tokens, setTokens] = useState<AuthTokens | null>(null);
    const [secondsLeft, setSecondsLeft] = useState(0);
    const [isResending, setIsResending] = useState(false);

    const phoneSchema = useMemo(() => createPhoneSchema(t), [t]);
    const codeSchema = useMemo(() => createCodeSchema(t), [t]);
    const newPasswordSchema = useMemo(
        () =>
            createNewPasswordSchema(t).refine((formData) => formData.password === formData.confirmPassword, {
                message: t('validation.password-mismatch'),
                path: ['confirmPassword'],
            }),
        [t],
    );

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<PhoneValues>({
        resolver: zodResolver(phoneSchema),
        mode: 'onTouched',
        defaultValues: { phone: data?.phone ?? '' },
    });

    const {
        register: registerCode,
        handleSubmit: handleCodeSubmit,
        formState: { errors: codeErrors, isSubmitting: isCodeSubmitting },
    } = useForm<CodeValues>({
        resolver: zodResolver(codeSchema),
        mode: 'onTouched',
    });

    const {
        register: registerNewPassword,
        handleSubmit: handleNewPasswordSubmit,
        formState: { errors: newPasswordErrors, isSubmitting: isNewPasswordSubmitting },
    } = useForm<NewPasswordValues>({
        resolver: zodResolver(newPasswordSchema),
        mode: 'onTouched',
    });

    useEffect(() => {
        if (step !== 'code') {
            return;
        }

        const id = setInterval(() => {
            setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        return (): void => clearInterval(id);
    }, [step]);

    const handleResetRequestError = (err: unknown): void => {
        const mnemonic = getAuthMnemonicFromError(err);

        if (mnemonic?.message === 'auth.code_request_cooldown') {
            toast.error(
                translateAuthMessage(t, mnemonic.message, 'auth.reset-password.error', {
                    seconds: mnemonic.secondsRemaining,
                }),
            );
            setSecondsLeft(mnemonic.secondsRemaining ?? RESEND_TIMEOUT_SECONDS);
            return;
        }

        if (!mnemonic || mnemonic.message === 'auth.internal_error') {
            Sentry.captureException(err);
        }
        toast.error(translateAuthMessage(t, mnemonic?.message, 'auth.reset-password.error'));
    };

    const onSubmitPhone: SubmitHandler<PhoneValues> = async (formData) => {
        try {
            const result = await storefrontAuth.resetPasswordRequest(formData.phone);

            setPhone(formData.phone);
            setSecondsLeft(result.seconds_remaining ?? RESEND_TIMEOUT_SECONDS);
            setStep('code');
        } catch (err) {
            handleResetRequestError(err);
        }
    };

    const onSubmitCode: SubmitHandler<CodeValues> = async (codeData) => {
        try {
            const result = await storefrontAuth.resetPasswordVerify({ phone, code: codeData.code });
            if (result.mode === 'verify fail') {
                toast.error(translateAuthMessage(t, result.message, 'auth.register.code.error'));
                return;
            }
            setTokens({ access_token: result.access_token, refresh_token: result.refresh_token });
            setStep('newPassword');
        } catch (err) {
            const mnemonic = getAuthMnemonicFromError(err);
            if (!mnemonic || mnemonic.message === 'auth.internal_error') {
                Sentry.captureException(err);
            }
            toast.error(translateAuthMessage(t, mnemonic?.message, 'auth.register.code.error'));
        }
    };

    const onSubmitNewPassword: SubmitHandler<NewPasswordValues> = async (formData) => {
        if (!tokens) {
            return;
        }

        try {
            // The new session starts only after the password is set, so this call uses the tokens from the code check.
            const result = await createBrowserApi({ getTokens: () => tokens }).auth.resetPasswordChange({
                newPassword: formData.password,
                newPasswordConfirmation: formData.confirmPassword,
            });
            await setSession(tokens);
            toast.success(translateAuthMessage(t, result.message, 'auth.password_changed'));
            onClose();
        } catch (err) {
            const mnemonic = getAuthMnemonicFromError(err);
            if (!mnemonic || mnemonic.message === 'auth.internal_error') {
                Sentry.captureException(err);
            }
            toast.error(translateAuthMessage(t, mnemonic?.message, 'auth.reset-password.newPassword.error'));
        }
    };

    const handleResend = async (): Promise<void> => {
        setIsResending(true);
        try {
            const result = await storefrontAuth.resetPasswordRequest(phone);
            setSecondsLeft(result.seconds_remaining ?? RESEND_TIMEOUT_SECONDS);
        } catch (err) {
            handleResetRequestError(err);
        } finally {
            setIsResending(false);
        }
    };

    if (step === 'newPassword') {
        return (
            <AuthModalLayout onClose={onClose} zIndex={zIndex} title={t('auth.reset-password.newPassword.title')}>
                <form
                    className={classNames(styles.form, 'mt-4')}
                    onSubmit={(event) => void handleNewPasswordSubmit(onSubmitNewPassword)(event)}
                >
                    <FormGroup
                        required
                        label={t('auth.field.password.label')}
                        errorText={newPasswordErrors.password?.message}
                    >
                        <PasswordInput
                            {...registerNewPassword('password')}
                            showPasswordLabel={t('form.password.show')}
                            hidePasswordLabel={t('form.password.hide')}
                            placeholder={t('auth.field.password.placeholder')}
                            error={Boolean(newPasswordErrors.password)}
                        />
                    </FormGroup>

                    <FormGroup
                        required
                        label={t('auth.field.confirm-password.label')}
                        errorText={newPasswordErrors.confirmPassword?.message}
                    >
                        <PasswordInput
                            {...registerNewPassword('confirmPassword')}
                            showPasswordLabel={t('form.password.show')}
                            hidePasswordLabel={t('form.password.hide')}
                            placeholder={t('auth.field.confirm-password.placeholder')}
                            error={Boolean(newPasswordErrors.confirmPassword)}
                        />
                    </FormGroup>

                    <Button className="mt-4" type="submit" size="lg" disabled={isNewPasswordSubmitting}>
                        {t('auth.reset-password.newPassword.submit')}
                    </Button>
                </form>
            </AuthModalLayout>
        );
    }

    if (step === 'code') {
        return (
            <AuthModalLayout onClose={onClose} zIndex={zIndex} title={t('auth.register.code.title')}>
                <Text align="center" size="md" className="mb-2">
                    {t('auth.register.code.sent-to', { phone })}
                </Text>

                <form
                    className={classNames(styles.form, 'mt-4')}
                    onSubmit={(event) => void handleCodeSubmit(onSubmitCode)(event)}
                >
                    <FormGroup label={t('auth.register.code.label')} errorText={codeErrors.code?.message}>
                        <Input
                            {...registerCode('code', {
                                onChange: (e: ChangeEvent<HTMLInputElement>) => {
                                    e.target.value = e.target.value.replace(/\D/g, '');
                                },
                            })}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            placeholder={t('auth.register.code.placeholder')}
                            error={Boolean(codeErrors.code)}
                        />
                    </FormGroup>

                    <Button className="mb-2" type="submit" size="lg" disabled={isCodeSubmitting}>
                        {t('auth.register.code.submit')}
                    </Button>

                    <Text align="center" color="muted" className={styles.resend}>
                        {secondsLeft > 0 ? (
                            t('auth.register.code.resend-timer', { time: formatCountdown(secondsLeft) })
                        ) : (
                            <button
                                type="button"
                                className={styles.link}
                                disabled={isResending}
                                onClick={() => void handleResend()}
                            >
                                {t('auth.register.code.resend')}
                            </button>
                        )}
                    </Text>
                </form>
            </AuthModalLayout>
        );
    }

    return (
        <AuthModalLayout onClose={onClose} zIndex={zIndex} title={t('auth.reset-password.phone.title')}>
            <Text align="center" size="md" className="mb-2">
                {t('auth.reset-password.phone.description')}
            </Text>

            <form
                className={classNames(styles.form, 'mt-4')}
                onSubmit={(event) => void handleSubmit(onSubmitPhone)(event)}
            >
                <FormGroup required label={t('auth.field.phone.label')} errorText={errors.phone?.message}>
                    <Controller
                        name="phone"
                        control={control}
                        render={({ field }) => (
                            <PhoneInput
                                value={field.value}
                                onChange={field.onChange}
                                defaultCountry={country.code.toLowerCase() as CountryIso2}
                                locale={i18n.language}
                                countryButtonLabel={t('form.phone.country-button')}
                                countryListLabel={t('form.phone.country-list')}
                                error={Boolean(errors.phone)}
                                placeholder={t('auth.field.phone.placeholder')}
                            />
                        )}
                    />
                </FormGroup>

                <Button className="mt-4" type="submit" size="lg" disabled={isSubmitting}>
                    {t('auth.reset-password.phone.submit')}
                </Button>
            </form>
        </AuthModalLayout>
    );
}
